// "Draw the missing column" questions (15 Oct 2026). The question shows a
// column graph with some columns missing, usually beside a table, and the
// student sets the height of each missing column. It is sent the way every
// list answer is: as strings that the marker compares with the answer key as
// a set. So an answer and its key must be written the same way, and the
// functions here are the one place that decides how.
//
// - The question names the columns to draw: `build` is a list of column
//   labels. In those columns, every column that stands at 0 in the picture
//   is one to draw. (A two-set graph may already show one of the pair.)
// - One drawn column is "bar=<column>.<set>=<height>", the column and the set
//   counted from 0 ("bar=2.0=35").
// - A column moves by half a mark of the scale, or by a whole mark when half
//   a mark is not a whole number.
//
// It cannot be mistaken for a several-boxes or sort entry, whose id before
// the "=" is always a single letter.
import {
  readPracticeDiagram,
  type PracticeDiagramSpec,
} from './practice-diagram-data';

type ColumnGraph = Extract<
  PracticeDiagramSpec,
  { type: 'column_graph' | 'line_chart' }
>;
type Table = Extract<PracticeDiagramSpec, { type: 'table' }>;

export type BuildBar = {
  // "<column>.<set>", both counted from 0.
  id: string;
  column: number;
  series: number;
  // "Plums", or "Tennis, Girls" on a graph with two sets of columns.
  label: string;
};

export type BuildQuestion = {
  graph: ColumnGraph;
  table: Table | null;
  bars: BuildBar[];
  // How far one press moves a column.
  unit: number;
};

// At most this many columns to draw in one question.
const MAX_BARS = 6;

function readColumnGraph(picture: unknown): ColumnGraph | null {
  const diagram = readPracticeDiagram({ diagram: picture });
  return diagram?.type === 'column_graph' ? diagram : null;
}

function unitOf(graph: ColumnGraph): number {
  return graph.step % 2 === 0 ? graph.step / 2 : graph.step;
}

function barsOf(graph: ColumnGraph, build: unknown): BuildBar[] | null {
  if (!Array.isArray(build) || build.length === 0) return null;
  if (new Set(build).size !== build.length) return null;
  const bars: BuildBar[] = [];
  for (const name of build as unknown[]) {
    const column = graph.columns.findIndex((entry) => entry.label === name);
    const values = graph.columns[column]?.values;
    if (!values) return null;
    const before = bars.length;
    for (const [series, value] of values.entries()) {
      if (value !== 0) continue;
      const set = graph.series?.[series];
      bars.push({
        id: `${String(column)}.${String(series)}`,
        column,
        series,
        label: set ? `${String(name)}, ${set}` : String(name),
      });
    }
    if (bars.length === before) return null;
  }
  return bars.length <= MAX_BARS ? bars : null;
}

// The question's graph, table and columns to draw, or null. A table that is
// given but cannot be read makes the whole question unreadable: the student
// could not answer without it.
export function readBuildGraph(content: unknown): BuildQuestion | null {
  if (typeof content !== 'object' || content === null) return null;
  const record = content as Record<string, unknown>;
  const graph = readColumnGraph(record.diagram);
  if (!graph) return null;
  const bars = barsOf(graph, record.build);
  if (!bars) return null;
  let table: Table | null = null;
  if (record.table !== undefined && record.table !== null) {
    const read = readPracticeDiagram({ diagram: record.table });
    if (read?.type !== 'table') return null;
    table = read;
  }
  return { graph, table, bars, unit: unitOf(graph) };
}

export function barEntry(id: string, height: number): string {
  return `bar=${id}=${String(height)}`;
}

export function parseBarEntry(
  entry: unknown,
): { id: string; height: number } | null {
  if (typeof entry !== 'string') return null;
  const match = /^bar=(\d{1,2}\.\d)=(\d{1,5})$/.exec(entry);
  return match?.[1] ? { id: match[1], height: Number(match[2]) } : null;
}

// The drawn columns as they are kept while a student works: "<id>=<height>"
// on each line. A column not listed stands at 0.
export function splitBuilt(
  text: string,
  question: BuildQuestion,
): Map<string, number> {
  const heights = new Map<string, number>();
  for (const line of text.split('\n')) {
    const parsed = parseBarEntry(`bar=${line}`);
    if (
      parsed &&
      question.bars.some((bar) => bar.id === parsed.id) &&
      parsed.height > 0 &&
      parsed.height <= question.graph.max &&
      parsed.height % question.unit === 0
    )
      heights.set(parsed.id, parsed.height);
  }
  return heights;
}

// One press of Taller (1) or Shorter (-1) on one column, as the new text.
export function stepBuilt(
  text: string,
  question: BuildQuestion,
  id: string,
  direction: 1 | -1,
): string {
  const heights = splitBuilt(text, question);
  const next = Math.min(
    question.graph.max,
    Math.max(0, (heights.get(id) ?? 0) + direction * question.unit),
  );
  heights.set(id, next);
  return question.bars
    .filter((bar) => (heights.get(bar.id) ?? 0) > 0)
    .map((bar) => `${bar.id}=${String(heights.get(bar.id))}`)
    .join('\n');
}

// What is sent, in one agreed order. Null until every column is drawn.
export function buildOrder(
  text: string,
  question: BuildQuestion,
): string[] | null {
  const heights = splitBuilt(text, question);
  if (heights.size !== question.bars.length) return null;
  return question.bars
    .map((bar) => barEntry(bar.id, heights.get(bar.id) ?? 0))
    .sort();
}

// The graph with the student's columns drawn in.
export function builtGraph(text: string, question: BuildQuestion): ColumnGraph {
  const heights = splitBuilt(text, question);
  return {
    ...question.graph,
    columns: question.graph.columns.map((column, index) => ({
      ...column,
      values: column.values.map(
        (value, series) =>
          heights.get(`${String(index)}.${String(series)}`) ?? value,
      ),
    })),
  };
}

// An answer key from the question bank, for whatever loads the questions.
// `answer` gives each column to draw by its label: one height, or one for
// each set. `picture` is the graph as the bank writes it. Returns the list
// for the question's `build` and its key, or null when the answer does not
// fit the picture: a column that is not there, a height the student could
// not reach, or a height that disagrees with a column already drawn.
export function buildGraphKey(
  answer: unknown,
  picture: unknown,
): { build: string[]; key: string[] } | null {
  const graph = readColumnGraph(picture);
  if (!graph || typeof answer !== 'object' || answer === null) return null;
  if (Array.isArray(answer)) return null;
  const build = Object.keys(answer);
  const bars = barsOf(graph, build);
  if (!bars) return null;
  const unit = unitOf(graph);
  const wanted = answer as Record<string, unknown>;
  const key: string[] = [];
  for (const name of build) {
    const column = graph.columns.findIndex((entry) => entry.label === name);
    const shown = graph.columns[column]?.values ?? [];
    const given = wanted[name];
    const heights: unknown[] = Array.isArray(given) ? given : [given];
    if (heights.length !== shown.length) return null;
    for (const [series, height] of heights.entries()) {
      if (typeof height !== 'number' || !Number.isInteger(height)) return null;
      if (shown[series] !== 0) {
        if (shown[series] !== height) return null;
        continue;
      }
      if (height <= 0 || height > graph.max || height % unit !== 0) return null;
      key.push(barEntry(`${String(column)}.${String(series)}`, height));
    }
  }
  return key.length === bars.length ? { build, key: key.sort() } : null;
}
